import { useState } from "react";
import {
  JsonFormsDispatch,
  useJsonForms,
  withJsonFormsLayoutProps,
} from "@jsonforms/react";
import { Categorization, StatePropsOfLayout } from "@jsonforms/core";
import { materialCells } from "@jsonforms/material-renderers";
import { Button } from "../../../../../../shadcn/components/ui/button";
import { StepperComponent } from "../../StepperComponent";
import CustomBaseRenderers from "./CustomBaseRenderer";
import { submitPYPData, resolveStateAndCity } from "services";
import { useRouter } from "next/router";
import customValidator from "../../CustomValidator";

const isPincodeBasedFetchingEnabled =
  process.env.NEXT_PUBLIC_ENABLE_PINCODE_BASED_FETCHING === "true";

const customRenderers = CustomBaseRenderers;

// Check if state/city needs resolution (not in proper {uuid, label, value} format)
const needsStateCityResolution = (formData: any): boolean => {
  if (!isPincodeBasedFetchingEnabled || !formData) return false;

  const { state, city } = formData;

  // Check if state needs resolution
  const stateNeedsResolution =
    !state?.uuid ||
    (typeof state === "string" && state) ||
    (state?.name && !state?.uuid);

  // Check if city needs resolution
  const cityNeedsResolution =
    !city?.uuid ||
    (typeof city === "string" && city) ||
    (city?.name && !city?.uuid);

  return stateNeedsResolution || cityNeedsResolution;
};

// Resolve state and city to proper {uuid, label, value} format
const resolveStateCityForSubmission = async (formData: any): Promise<any> => {
  if (!isPincodeBasedFetchingEnabled || !formData) return formData;

  const { state, city, district } = formData;

  // Check if resolution is needed
  if (!needsStateCityResolution(formData)) {
    return formData;
  }

  try {
    // Call backend API to resolve state/city
    // Pass district name if available (needed for city creation)
    const resolved = await resolveStateAndCity(
      state,
      city,
      district?.name || district?.label,
    );

    if (resolved?.error) {
      console.error("Error resolving state/city:", resolved.error);
      return formData;
    }

    // Return form data with resolved state/city
    return {
      ...formData,
      state: resolved?.state || formData.state,
      city: resolved?.city || formData.city,
    };
  } catch (error) {
    console.error("Error calling resolveStateAndCity:", error);
    return formData;
  }
};

const CustomStepper = ({ uischema, schema, data }: StatePropsOfLayout) => {
  const router = useRouter();
  const uischemaFile = uischema as Categorization;
  const [activeStep, setActiveStep] = useState(0);
  const totalSteps = uischemaFile?.elements.length;
  const formTitle = uischemaFile?.options?.stepsData?.formTitle;
  const buttonTitle = uischemaFile?.options?.stepsData?.buttonTitle;
  const stepsTitle = uischemaFile?.options?.stepsData?.stepsTitle;
  const ctx = useJsonForms();

  const submitData = async () => {
    try {
      // Resolve state/city to proper format before submission
      const resolvedData = await resolveStateCityForSubmission(data);

      const payload = {
        uuid: uischema?.options?.property_uuid,
        status: "pending",
        json_form_data: resolvedData,
      };

      if (uischema?.options?.role === "cirp_professional") {
        payload["json_form_data"]["instituteName"] = {};
        payload["json_form_data"]["instituteBranch"] = {};
        payload["json_form_data"]["scheduledAuction"] = {
          label: "Yes",
          value: "yes",
        };
      }

      if (
        uischema?.options?.role === "property_agent" ||
        uischema?.options?.role === "owner"
      ) {
        payload["json_form_data"]["scheduledAuction"] = {
          label: "No",
          value: "no",
        };
      }

      if (activeStep === totalSteps - 1) {
        payload["status"] = "submitted";
        const response: any = await submitPYPData(payload);
        if (response)
          router.push({
            pathname: `/post-your-property/${uischema?.options?.property_uuid}/success-page`,
          });
      } else {
        const response: any = await submitPYPData(payload);
        if (response) {
          setActiveStep(activeStep + 1);
          window.scrollTo({ top: 0, behavior: "auto" });
        }
      }
    } catch (err) {
      console.log("error", err);
    }
  };

  const handleClick = () => {
    const isValid = customValidator(
      uischemaFile?.elements[activeStep],
      ctx,
      activeStep,
    );

    if (isValid) {
      console.log("validation success");
      submitData();
    } else console.log("errors found");

    console.log("ctx,", ctx);
  };

  return (
    <div className="px-customPaddingMobile  lg:px-customPadding mt-8 2xl:w-[1340px] 2xl:mx-auto 2xl:px-0">
      <StepperComponent
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        totalSteps={totalSteps}
        stepsTitle={stepsTitle}
        outerClassName="lg:hidden flex"
      />

      <div className="clip-path-mobilePYPClipPolygonTopRight lg:clip-path-mobileClipPolygonTopRight bg-brand-lightPink mt-[68px] lg:mt-10  py-11 lg:py-9 px-4  lg:pl-7 lg:pr-9">
        <div className="lg:flex w-full">
          <StepperComponent
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            totalSteps={totalSteps}
            stepsTitle={stepsTitle}
            outerClassName="hidden lg:flex"
          />

          <div className="max-w-[848px] w-full">
            <p className="font-openSans font-normal text-[22px] leading-[29px] lg:text-web-h3 lg:leading-[38px] text-brand-bgBlack">
              {formTitle[activeStep]}
            </p>
            <JsonFormsDispatch
              uischema={uischemaFile?.elements[activeStep]}
              renderers={customRenderers}
              cells={materialCells}
            />
            <Button className="w-full mt-12" onClick={handleClick}>
              {buttonTitle[activeStep]}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withJsonFormsLayoutProps(CustomStepper);
